import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, TrendingUp, TrendingDown, ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import type { Mangara, Colheita, CorFita, Ciclo } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";

import { PageHeader } from "@/components/layout/page-header";
import { DataCard } from "@/components/data/data-card";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
type CicloWithRelations = Ciclo & {
  corFita?: CorFita;
  dataPrevisaoInicio?: string | null;
  dataPrevisaoFim?: string | null;
};

export default function RelatoriosProdutividadeFamiliaPage() {
  // Filter states
  const [filtroPeriodo, setFiltroPeriodo] = useState<string>("tudo");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");

  const { data: ciclos, isLoading: loadingCiclos } = useQuery<CicloWithRelations[]>({
    queryKey: ["/api/ciclos"],
  });

  const { setores, isLoading: loadingSetores } = useSetores({ somenteProdutivos: true });

  const { data: mangaras } = useQuery<Mangara[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<Colheita[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: cores } = useQuery<CorFita[]>({
    queryKey: ["/api/cores"],
  });

  const isLoading = loadingCiclos || loadingSetores;

  // Helper: get cycle predicted year from mangaras
  const getCycleAnoPrevisto = (cicloId: string): number => {
    const cicloMangaras = mangaras?.filter(m => m.cicloId === cicloId) || [];
    if (cicloMangaras.length > 0 && cicloMangaras[0].anoSemanaPrevistaColheita) {
      return cicloMangaras[0].anoSemanaPrevistaColheita;
    }
    return new Date().getFullYear();
  };

  // Helper: derive the period window used to filter both ciclos and bare colheitas
  // Returns null for "tudo" (no filtering), or { anoAlvo } for year filters,
  // or { inicio, fim } for date-range filters.
  const getPeriodoWindow = (): { anoAlvo: number } | { inicio: Date; fim: Date } | null => {
    const hoje = new Date();
    if (filtroPeriodo === "tudo") return null;
    if (filtroPeriodo === "ano_atual") return { anoAlvo: hoje.getFullYear() };
    if (filtroPeriodo === "ano_anterior") return { anoAlvo: hoje.getFullYear() - 1 };
    if (filtroPeriodo === "mes_atual") {
      return {
        inicio: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
        fim: new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0, 23, 59, 59, 999),
      };
    }
    if (filtroPeriodo === "mes_anterior") {
      return {
        inicio: new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1),
        fim: new Date(hoje.getFullYear(), hoje.getMonth(), 0, 23, 59, 59, 999),
      };
    }
    if (filtroPeriodo === "periodo_especifico" && dataInicio && dataFim) {
      return {
        inicio: new Date(dataInicio + "T00:00:00"),
        fim: new Date(dataFim + "T23:59:59"),
      };
    }
    return null;
  };

  // Filter cycles based on period
  const filteredCiclos = useMemo(() => {
    if (!ciclos) return [];
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    return ciclos.filter(ciclo => {
      // Period filter
      if (filtroPeriodo !== "tudo") {
        const anoPrevisto = getCycleAnoPrevisto(ciclo.id);
        
        if (filtroPeriodo === "ano_atual") {
          if (anoPrevisto !== hoje.getFullYear()) return false;
        } else if (filtroPeriodo === "ano_anterior") {
          if (anoPrevisto !== hoje.getFullYear() - 1) return false;
        } else if (filtroPeriodo === "mes_atual" || filtroPeriodo === "mes_anterior") {
          const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
          const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
          
          if (!dataPrevisaoInicio || !dataPrevisaoFim) return false;
          
          let inicioMes: Date, fimMes: Date;
          if (filtroPeriodo === "mes_atual") {
            inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0, 23, 59, 59, 999);
          } else {
            inicioMes = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
            fimMes = new Date(hoje.getFullYear(), hoje.getMonth(), 0, 23, 59, 59, 999);
          }
          
          if (!(dataPrevisaoInicio <= fimMes && dataPrevisaoFim >= inicioMes)) return false;
        } else if (filtroPeriodo === "periodo_especifico" && dataInicio && dataFim) {
          const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
          const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
          
          if (!dataPrevisaoInicio || !dataPrevisaoFim) return false;
          
          const inicio = new Date(dataInicio + "T00:00:00");
          const fim = new Date(dataFim + "T23:59:59");
          
          if (!(dataPrevisaoInicio <= fim && dataPrevisaoFim >= inicio)) return false;
        }
      }
      
      return true;
    });
  }, [ciclos, mangaras, filtroPeriodo, dataInicio, dataFim]);

  // Calcular performance por setor (usando ciclos filtrados)
  const performancePorSetor = useMemo(() => {
    const filteredCicloIds = new Set(filteredCiclos.map(c => c.id));
    const periodoWindow = getPeriodoWindow();

    // Check if a bare colheita (cicloId===null) falls within the active period window
    const colheitaSemCicloNoPeriodo = (c: Colheita): boolean => {
      if (!periodoWindow) return true; // "tudo"
      const dataC = c.dataColheita ? new Date(c.dataColheita) : null;
      if (!dataC) return false;
      if ("anoAlvo" in periodoWindow) {
        const ano = (c as any).anoSemanaColheita ?? dataC.getFullYear();
        return ano === periodoWindow.anoAlvo;
      }
      return dataC >= periodoWindow.inicio && dataC <= periodoWindow.fim;
    };

    return setores?.map(setor => {
      // Filtrar mangarás e colheitas deste setor apenas para ciclos filtrados
      const mangarasSetor = mangaras?.filter(m => m.setorId === setor.id && filteredCicloIds.has(m.cicloId)) || [];
      const colheitasSetor = colheitas?.filter(c =>
        c.setorId === setor.id &&
        (c.cicloId !== null ? filteredCicloIds.has(c.cicloId) : colheitaSemCicloNoPeriodo(c))
      ) || [];
      
      const totalMarcado = mangarasSetor.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
      const totalColhido = colheitasSetor.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
      
      const percentualColheita = totalMarcado > 0 ? (totalColhido / totalMarcado) * 100 : 0;
      
      // Performance esperada: cachos por muda
      const cachoPorMuda = setor.quantidadeMudas > 0 ? totalMarcado / setor.quantidadeMudas : 0;
      
      // Performance por fita dentro do setor (apenas ciclos filtrados)
      const fitasPorSetor = filteredCiclos.filter(ciclo => {
        const mangarasCiclo = mangaras?.filter(m => m.cicloId === ciclo.id && m.setorId === setor.id) || [];
        return mangarasCiclo.length > 0;
      }).map(ciclo => {
        const mangarasCiclo = mangaras?.filter(m => m.cicloId === ciclo.id && m.setorId === setor.id) || [];
        const colheitasCiclo = colheitas?.filter(c => c.cicloId === ciclo.id && c.setorId === setor.id) || [];
        
        const totalCachosCiclo = mangarasCiclo.reduce((sum, m) => sum + (m.quantidadeCachos || 0), 0);
        const totalColhidoCiclo = colheitasCiclo.reduce((sum, c) => sum + (c.quantidadeCachos || 0), 0);
        const percentualCiclo = totalCachosCiclo > 0 ? (totalColhidoCiclo / totalCachosCiclo) * 100 : 0;

        const cor = cores?.find(c => c.id === ciclo.corFitaId);
        
        return {
          codigo: ciclo.codigo,
          corNome: cor?.nome || '',
          hexColor: cor?.hexColor || '#000',
          totalCachos: totalCachosCiclo,
          totalColhido: totalColhidoCiclo,
          percentualColheita: percentualCiclo,
        };
      }).filter(f => f.totalCachos > 0) || [];

      return {
        ...setor,
        totalMarcado,
        totalColhido,
        percentualColheita,
        cachoPorMuda,
        fitasPorSetor,
      };
    }).filter(s => s.totalMarcado > 0 || s.fitasPorSetor.length > 0)
      .sort((a, b) => b.percentualColheita - a.percentualColheita) || [];
  }, [setores, mangaras, colheitas, filteredCiclos, cores, filtroPeriodo, dataInicio, dataFim]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start gap-3">
        <Link href="/cacho/relatorios">
          <Button variant="ghost" size="icon" data-testid="button-voltar-relatorios">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <PageHeader
            titulo="Produtividade da família"
            descricao="Desempenho de colheita e produtividade por área"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Period Filter */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Período</Label>
          <Select value={filtroPeriodo} onValueChange={setFiltroPeriodo}>
            <SelectTrigger className="w-[180px]" data-testid="select-filtro-periodo">
              <SelectValue placeholder="Tudo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tudo">Tudo</SelectItem>
              <SelectItem value="ano_atual">Ano Atual</SelectItem>
              <SelectItem value="ano_anterior">Ano Anterior</SelectItem>
              <SelectItem value="mes_atual">Mês Atual</SelectItem>
              <SelectItem value="mes_anterior">Mês Anterior</SelectItem>
              <SelectItem value="periodo_especifico">Período Específico</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date Range (for specific period) */}
        {filtroPeriodo === "periodo_especifico" && (
          <>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Início</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-[150px] text-xs"
                data-testid="input-data-inicio"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fim</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-[150px] text-xs"
                data-testid="input-data-fim"
              />
            </div>
          </>
        )}

        {/* Results Count */}
        <div className="ml-auto text-sm text-muted-foreground">
          {performancePorSetor.length} {performancePorSetor.length === 1 ? 'setor' : 'setores'}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <PageSkeleton variante="cards" linhas={3} />
      ) : performancePorSetor.length === 0 ? (
        <EmptyState
          titulo="Nenhum setor cadastrado"
          descricao="A produtividade é por área — cadastre os setores para a conta de cachos por muda existir."
          testId="empty-produtividade"
        />
      ) : (
        <div className="space-y-3">
          {performancePorSetor.map((setor) => (
            <SetorPerformanceCard key={setor.id} setor={setor} />
          ))}
        </div>
      )}
    </div>
  );
}

// Componente para card de performance do setor (com collapsible)
function SetorPerformanceCard({ setor }: { setor: any }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <DataCard
        titulo={setor.nome}
        meta={
          <>
            {/* Variedade é CATEGORIA: outline neutro. */}
            <Badge variant="outline" className="text-xs font-normal mr-2">{setor.variedade?.nome}</Badge>
            {setor.fitasPorSetor.length > 0 && (
              <>{setor.fitasPorSetor.length} {setor.fitasPorSetor.length === 1 ? 'fita' : 'fitas'}</>
            )}
          </>
        }
        acao={
          setor.fitasPorSetor.length > 0 ? (
            isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )
          ) : undefined
        }
        testId={`setor-performance-${setor.id}`}
      >
        <CollapsibleTrigger asChild>
          <div className="p-3 md:p-4 cursor-pointer hover-elevate">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Mudas</p>
                    <p className="font-semibold">{setor.quantidadeMudas?.toLocaleString() || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Marcado</p>
                    <p className="font-semibold">{setor.totalMarcado.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Colhido</p>
                    <p className="font-semibold text-sucesso">
                      {setor.totalColhido.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">% Colheita</p>
                    <p className="font-semibold" data-testid={`setor-percentual-${setor.id}`}>
                      {setor.percentualColheita.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Cachos/Muda</p>
                    <p className="font-semibold flex items-center gap-1">
                      {setor.cachoPorMuda.toFixed(2)}
                      {setor.cachoPorMuda >= 0.8 ? (
                        <TrendingUp className="h-3 w-3 text-sucesso" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-atencao" />
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="hidden md:block w-32 shrink-0">
                <Progress value={setor.percentualColheita} className="h-2" />
              </div>
            </div>
          </div>
        </CollapsibleTrigger>

        {setor.fitasPorSetor.length > 0 && (
          <CollapsibleContent>
            <div className="pt-0 pb-3 md:pb-4 px-3 md:px-4">
              <div className="border-t pt-3 space-y-2">
                <p className="text-xs text-muted-foreground font-medium mb-2">Detalhamento por Fita:</p>
                {setor.fitasPorSetor.map((fita: any, idx: number) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 rounded-md bg-muted/30"
                    data-testid={`setor-fita-${setor.id}-${idx}`}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <div 
                        className="w-3 h-3 rounded-sm flex-shrink-0" 
                        style={{ backgroundColor: fita.hexColor }}
                      />
                      <span className="text-xs font-mono font-medium">{fita.codigo}</span>
                      <span className="text-xs text-muted-foreground">({fita.corNome})</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="text-right">
                        <span className="text-muted-foreground">Marcado: </span>
                        <span className="font-semibold">{fita.totalCachos}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground">Colhido: </span>
                        <span className="font-semibold text-sucesso">
                          {fita.totalColhido}
                        </span>
                      </div>
                      <div className="text-right min-w-[60px]">
                        <span className="font-semibold">{fita.percentualColheita.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleContent>
        )}
      </DataCard>
    </Collapsible>
  );
}
